<template>
    <div class="cv3-customerbar">
        <div class="cv3-selector" ref="root">
            <!-- Vente clôturée : le client de la commande est figé comme le reste (NF525). -->
            <button type="button" class="cv3-selector-btn" :disabled="locked" @click="toggle">
                <span class="cv3-avatar">
                    <UserIcon :size="16" />
                </span>
                <span class="cv3-selector-text">
                    <span class="cv3-overline-sm">{{ $t('checkout.v3.client_label') }}</span>
                    <span class="cv3-selector-name">{{ displayName }}</span>
                </span>
                <LockIcon v-if="locked" :size="15" class="cv3-chevron" />
                <ChevronDownIcon v-else :size="18" class="cv3-chevron" />
            </button>

            <div v-if="open" class="cv3-dropdown">
                <div class="cv3-search-mini">
                    <SearchIcon :size="16" />
                    <input
                        ref="search"
                        v-model="query"
                        type="text"
                        :placeholder="$t('checkout.v3.search_client_placeholder')"
                        @input="onSearch"
                    />
                </div>

                <button type="button" class="cv3-cust-row" :class="{ active: isPassageActive }" @click="pickPassage">
                    <span class="cv3-avatar sm"><UserIcon :size="14" /></span>
                    <span class="cv3-cust-info">
                        <span class="cv3-cust-name">{{ $t('checkout.v3.guest_client') }}</span>
                        <span class="cv3-cust-mail">{{ $t('checkout.v3.no_email') }}</span>
                    </span>
                    <CheckIcon v-if="isPassageActive" :size="16" class="cv3-check" />
                </button>

                <p class="cv3-group-label">{{ $t('checkout.v3.registered_clients') }}</p>
                <div class="cv3-cust-list">
                    <button
                        v-for="c in filteredCustomers"
                        :key="c.id"
                        type="button"
                        class="cv3-cust-row"
                        :class="{ active: store.userId === c.id }"
                        @click="pick(c)"
                    >
                        <span class="cv3-avatar sm initials">{{ initials(c) }}</span>
                        <span class="cv3-cust-info">
                            <span class="cv3-cust-name">{{ fullName(c) }}</span>
                            <span class="cv3-cust-mail">{{ c.email }}</span>
                        </span>
                        <CheckIcon v-if="store.userId === c.id" :size="16" class="cv3-check" />
                    </button>
                    <p v-if="loading" class="cv3-empty">{{ $t('checkout.v3.loading') }}</p>
                    <p v-else-if="!filteredCustomers.length" class="cv3-empty">{{ $t('checkout.v3.no_clients') }}</p>
                </div>
            </div>
        </div>

        <button type="button" class="cv3-new-client" @click="openAddClient" :disabled="store.busy || locked">
            <PlusIcon :size="17" />
        </button>

        <AddClientModal :store="store" :open="showAdd" @close="showAdd = false" />
    </div>
</template>

<script>
import {
    User as UserIcon,
    ChevronDown as ChevronDownIcon,
    Search as SearchIcon,
    Check as CheckIcon,
    Plus as PlusIcon,
    Lock as LockIcon,
} from 'lucide-vue'
import AddClientModal from './AddClientModal.vue'

export default {
    name: 'CustomerBar',
    components: { UserIcon, ChevronDownIcon, SearchIcon, CheckIcon, PlusIcon, LockIcon, AddClientModal },
    props: { store: { type: Object, required: true } },
    data() {
        return {
            open: false,
            query: '',
            results: [], // customers from the V3 customer search endpoint
            loading: false,
            loaded: false,
            searchTimer: null,
            searchSeq: 0, // drops responses that land after a newer query was fired
            showAdd: false, // "Nouveau client" creation modal
        }
    },
    computed: {
        // Vente clôturée (status_id 7 / signée) : le client de la commande est figé (NF525).
        locked() {
            return !!this.store.isClotured
        },
        displayName() {
            const c = this.store.currentCustomer
            if (!c || c === false) return this.$t('checkout.v3.guest_client')
            if (c.is_guest || (!c.email && !c.first_name && !c.last_name)) return this.$t('checkout.v3.guest_client')
            return this.fullName(c) || this.$t('checkout.v3.guest_client')
        },
        isPassageActive() {
            const c = this.store.currentCustomer
            return !c || c === false || c.is_guest
        },
        filteredCustomers() {
            return this.results || []
        },
    },
    mounted() {
        document.addEventListener('click', this.onOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onOutside)
    },
    methods: {
        toggle() {
            this.open = !this.open
            if (this.open) {
                this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
                if (!this.loaded) this.load('')
            }
        },
        async load(q) {
            const seq = ++this.searchSeq
            this.loading = true
            try {
                const data = await this.store.searchCustomers(q)
                if (seq !== this.searchSeq) return
                this.results = Array.isArray(data) ? data : []
                this.loaded = true
            } catch (e) {
                if (seq === this.searchSeq) this.results = []
            } finally {
                if (seq === this.searchSeq) this.loading = false
            }
        },
        onOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) this.open = false
        },
        fullName(c) {
            return [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
        },
        initials(c) {
            const f = (c.first_name || '').charAt(0)
            const l = (c.last_name || '').charAt(0)
            return (f + l).toUpperCase() || '?'
        },
        onSearch() {
            clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(() => this.load(this.query), 350)
        },
        async pick(c) {
            this.open = false
            try {
                await this.store.selectCustomer(c)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_selecting_client'))
            }
        },
        async pickPassage() {
            this.open = false
            try {
                await this.store.newWalkIn()
                this.$toast && this.$toast.success(this.$t('checkout.v3.walk_in_customer'))
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_creating_client'))
            }
        },
        openAddClient() {
            this.open = false
            this.showAdd = true
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-customerbar {
    display: flex;
    gap: 12px;
    align-items: stretch;
}
.cv3-selector {
    position: relative;
    flex: 1 1 auto;
    max-width: 30%;
    min-width: max-content;
}
.cv3-selector-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 8px 14px 8px 8px;
    border-radius: 13px;
    border: 1px solid var(--cv3-border);
    background: var(--cv3-selector);
    box-shadow: var(--cv3-shadow);
    color: var(--cv3-text);
}
.cv3-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #4d4e5d;
    color: #fff;
    flex-shrink: 0;
    &.sm {
        width: 30px;
        height: 30px;
        border-radius: 8px;
    }
    &.initials {
        background: var(--cv3-icon-tint);
        font-size: 12px;
        font-weight: 700;
        font-family: 'Plus Jakarta Sans', sans-serif;
    }
}
.cv3-selector-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
}
.cv3-overline-sm {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--cv3-muted);
}
.cv3-selector-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--cv3-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}
.cv3-chevron {
    color: var(--cv3-muted);
    flex-shrink: 0;
}

.cv3-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 40;
    background: var(--cv3-panel);
    border: 1px solid var(--cv3-border-input);
    border-radius: 14px;
    padding: 12px;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.5);
    max-height: 420px;
    overflow: auto;
}
.cv3-search-mini {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--cv3-border-input);
    color: var(--cv3-muted);
    margin-bottom: 8px;
    input {
        flex: 1 1 auto;
        background: transparent;
        border: 0;
        outline: 0;
        color: var(--cv3-text);
        font-size: 14px;
        &::placeholder {
            color: var(--cv3-muted);
        }
    }
}
.cv3-cust-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border-radius: 10px;
    border: 0;
    background: transparent;
    color: var(--cv3-text);
    text-align: left;
    &:hover {
        background: rgba(255, 255, 255, 0.04);
    }
    &.active {
        background: var(--cv3-icon-tint);
    }
}
.cv3-cust-info {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}
.cv3-cust-name {
    font-weight: 700;
    font-size: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
}
.cv3-cust-mail {
    font-size: 12px;
    color: var(--cv3-muted);
}
.cv3-check {
    color: var(--cv3-accent);
}
.cv3-group-label {
    margin: 10px 4px 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--cv3-muted);
}
.cv3-empty {
    padding: 12px;
    text-align: center;
    color: var(--cv3-muted);
    font-size: 13px;
}
.cv3-new-client {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 16px;
    border-radius: 12px;
    border: 0;
    background: var(--cv3-selector);
    box-shadow: var(--cv3-shadow);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}
</style>
